import React from 'react';
import { ArrowUpDown, Download, Gauge } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import type {
  PerformanceResultRow,
  PerformanceScheduleDetail,
} from '../../../types/performance.types';
import {
  formatGap,
  formatScore,
  gapTone,

  reviewStatusTone,
} from '../../../utils/performance';
import { InitialsAvatar, PerformancePill } from '../PerformancePrimitives';

interface ResultsTabProps {
  detail: PerformanceScheduleDetail;
  onCalibrate: (result: PerformanceResultRow) => void;
}

const downloadResultsCsv = (rows: PerformanceResultRow[], scheduleName: string) => {
  const head = [
    'Employee',
    'Department',
    'Job Role',
    'Manager',
    'Goals',
    'Weighting',
    'Self score',
    'Manager score',
    'Gap',
    'Final score',
    'Rating band',
    'Status',
  ];
  const lines = [
    head,
    ...rows.map((row) => [
      row.user.name,
      row.user.department || '',
      row.user.jobRole,
      row.user.managerName || '',
      row.goalsCount,
      `${row.weightTotal}%`,
      formatScore(row.selfScore),
      formatScore(row.managerScore),
      formatGap(row.gap),
      formatScore(row.finalScore),
      row.band?.label || '',
      row.status,
    ]),
  ];
  const csv = lines
    .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${scheduleName.replace(/[^\w-]+/g, '-')}-results.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const ResultsTab: React.FC<ResultsTabProps> = ({ detail, onCalibrate }) => {
  const [department, setDepartment] = React.useState('all');
  const [band, setBand] = React.useState('all');
  const [sort, setSort] = React.useState<'gap' | 'name'>('gap');

  const departments = Array.from(
    new Set(detail.results.map((row) => row.user.department).filter(Boolean))
  ) as string[];

  const rows = detail.results
    .filter((row) => department === 'all' || row.user.department === department)
    .filter((row) => band === 'all' || String(row.band?.score) === band)
    .sort((a, b) =>
      sort === 'gap'
        ? Math.abs(b.gap || 0) - Math.abs(a.gap || 0)
        : a.user.name.localeCompare(b.user.name)
    );

  const distribution = [...detail.levels]
    .sort((a, b) => b.score - a.score)
    .map((level) => ({
      label: level.label,
      manager: detail.results.filter((row) => row.band?.score === level.score).length,
      final: detail.results.filter((row) => row.band?.score === level.score).length,
    }));
  const max = Math.max(1, ...distribution.map((row) => row.manager));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Calibration & Results</h3>
          <p className="text-sm text-muted-foreground">
            {detail.schedule.status === 'Calibration'
              ? 'Calibration is open. Every change is a new version; the manager’s original ratings are kept.'
              : detail.schedule.status === 'Closed'
                ? 'Closed — results are read-only and visible to employees.'
                : 'Results appear once the end-of-year window opens. Final = manager score until calibration.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadResultsCsv(detail.results, detail.schedule.name)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rating distribution
          </p>
          <span className="text-xs text-muted-foreground">
            {detail.results.filter((row) => row.managerScore != null).length} manager-scored ·{' '}
            {detail.results.filter((row) => row.calibrated).length} calibrated
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {distribution.map((row) => (
            <div key={row.label} className="grid grid-cols-[minmax(120px,1fr)_minmax(0,2fr)_32px] items-center gap-2">
              <span className="truncate text-xs text-muted-foreground">{row.label}</span>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${(row.manager / max) * 100}%` }}
                />
              </div>
              <span className="text-right text-xs tabular-nums">{row.manager}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={band} onValueChange={setBand}>
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rating bands</SelectItem>
            {[...detail.levels].sort((a, b) => b.score - a.score).map((level) => (
              <SelectItem key={level.score} value={String(level.score)}>
                {level.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSort((current) => (current === 'gap' ? 'name' : 'gap'))}
        >
          <ArrowUpDown className="mr-1.5 h-3.5 w-3.5" />
          {sort === 'gap' ? 'Sorted by gap' : 'Sorted by name'}
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {rows.length} of {detail.results.length} people
        </span>
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-border bg-card lg:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium">Employee</th>
              <th className="px-3 py-2.5 text-left font-medium">Department</th>
              <th className="px-3 py-2.5 text-left font-medium">Weighting</th>
              <th className="px-3 py-2.5 text-right font-medium">Self</th>
              <th className="px-3 py-2.5 text-right font-medium">Manager</th>
              <th className="px-3 py-2.5 text-right font-medium">Gap</th>
              <th className="px-3 py-2.5 text-right font-medium">Final</th>
              <th className="px-3 py-2.5 text-left font-medium">Band</th>
              <th className="px-3 py-2.5 text-left font-medium">Status</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.user.id} className="border-t border-border">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <InitialsAvatar name={row.user.name} className="h-7 w-7" />
                    <div>
                      <div className="font-medium">{row.user.name}</div>
                      <div className="text-xs text-muted-foreground">{row.user.jobRole}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">{row.user.department || '–'}</td>
                <td className="px-3 py-2.5">
                  <PerformancePill tone={row.weightTotal === 100 ? 'success' : 'warning'}>
                    {row.weightTotal}%
                  </PerformancePill>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatScore(row.selfScore)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatScore(row.managerScore)}</td>
                <td className="px-3 py-2.5 text-right">
                  <PerformancePill tone={gapTone(row.gap)}>{formatGap(row.gap)}</PerformancePill>
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                  {formatScore(row.finalScore)}
                </td>
                <td className="px-3 py-2.5">
                  {row.band ? (
                    <PerformancePill tone={row.band.tone}>{row.band.label}</PerformancePill>
                  ) : (
                    '–'
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <PerformancePill tone={reviewStatusTone(row.status)}>{row.status}</PerformancePill>
                </td>
                <td className="px-3 py-2.5">
                  {detail.schedule.status === 'Calibration' && row.canCalibrate ? (
                    <Button size="sm" variant="outline" onClick={() => onCalibrate(row)}>
                      <Gauge className="mr-1.5 h-3.5 w-3.5" /> Calibrate
                    </Button>
                  ) : row.calibrationCount ? (
                    <Button size="sm" variant="ghost" onClick={() => onCalibrate(row)}>
                      History
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <div key={row.user.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <InitialsAvatar name={row.user.name} />
                <div>
                  <div className="text-sm font-semibold">{row.user.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.user.jobRole}
                    {row.user.department ? ` · ${row.user.department}` : ''}
                  </div>
                </div>
              </div>
              <PerformancePill tone={reviewStatusTone(row.status)}>{row.status}</PerformancePill>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
              <div>
                <p className="text-muted-foreground">Self</p>
                <p className="font-semibold tabular-nums">{formatScore(row.selfScore)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Manager</p>
                <p className="font-semibold tabular-nums">{formatScore(row.managerScore)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Gap</p>
                <p className="font-semibold tabular-nums">{formatGap(row.gap)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Final</p>
                <p className="font-semibold tabular-nums">{formatScore(row.finalScore)}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              {row.band ? (
                <PerformancePill tone={row.band.tone}>{row.band.label}</PerformancePill>
              ) : (
                <span />
              )}
              {detail.schedule.status === 'Calibration' && row.canCalibrate ? (
                <Button size="sm" variant="outline" onClick={() => onCalibrate(row)}>
                  Calibrate
                </Button>
              ) : row.calibrationCount ? (
                <Button size="sm" variant="ghost" onClick={() => onCalibrate(row)}>
                  History
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {!rows.length ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No results match these filters.
        </p>
      ) : null}
    </div>
  );
};
